import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navbar
    nav: {
      home: 'Home',
      services: 'Services',
      projects: 'Projects',
      about: 'About',
      contact: 'Contact',
      consultation: 'Consultation',
      startProject: 'Start Project',
    },
    // Hero
    hero: {
      tagline: 'Premium Residential Contractor',
      title1: 'Building',
      title2: 'Luxury Legacies',
      subtitle: 'Premium Residential Renovations in Philadelphia',
      cta1: 'Start Project',
      cta2: 'View Projects',
      scroll: 'Scroll',
      stat1Label: 'Projects',
      stat2Label: 'Years',
      stat3Label: 'Satisfaction',
    },
    // Services Section (Home)
    services: {
      tagline: 'Our Services',
      title: 'What We Manage',
      service1: { title: 'Designer Kitchens', subtitle: 'Kitchen Design' },
      service2: { title: 'Spa Bathrooms', subtitle: 'Luxury Bathrooms' },
      service3: { title: 'Full Renovations', subtitle: 'Full Renovations' },
      extras: ['Painting', 'Drywall', 'Maintenance', 'Lighting'],
    },
    // Projects Section
    projects: {
      tagline: 'Portfolio',
      title: 'Featured Projects',
      viewAll: 'View All',
      project1: { title: 'Chestnut Hill Residence', category: 'Full Renovation' },
      project2: { title: 'Rittenhouse Penthouse', category: 'Designer Kitchen' },
      project3: { title: 'Society Hill Villa', category: 'Spa Bathroom' },
      project4: { title: 'Washington Square Townhouse', category: 'Living Room' },
    },
    // Why Choose Us
    whyUs: {
      tagline: 'Our Difference',
      title: 'Why Choose Us',
      reason1: { title: 'Precision', subtitle: 'Precision', desc: 'Clear documentation of deliverables and technical specifications before start.' },
      reason2: { title: 'Transparency', subtitle: 'Transparency', desc: 'Direct communication, regular updates, and structured response.' },
      reason3: { title: 'Quality', subtitle: 'Quality', desc: 'Premium materials and architectural specification finishes.' },
      reason4: { title: 'Timeliness', subtitle: 'Timeliness', desc: 'Realistic schedules with clear milestones and deadline adherence.' },
      reason5: { title: 'Commitment', subtitle: 'Commitment', desc: 'Continuous supervision to ensure adherence to quality standards.' },
    },
    // About Section
    about: {
      tagline: 'About Us',
      title1: 'Building',
      title2: 'Excellence',
      description1: 'is a residential contracting company established in Philadelphia, specializing in professional management of interior improvement projects and luxury renovations.',
      description2: 'We operate with a structure of continuous supervision, documented processes, and commitment to precise execution according to established specifications.',
      description3: 'Our mission is to provide residential services with the level of professionalism, transparency, and project control that our clients expect from an established contractor.',
      cta: 'Learn More',
      values: {
        v1: { title: 'License & Insurance', desc: 'We operate with all required licenses and complete coverage.' },
        v2: { title: 'Quality Standards', desc: 'Strict adherence to construction codes and best practices.' },
        v3: { title: 'Professional Team', desc: 'Trained personnel with proven experience in their specialties.' },
        v4: { title: 'Total Commitment', desc: 'Focus on meeting timelines and agreed budgets.' },
      },
    },
    // Contact Section
    contact: {
      tagline: 'Contact',
      title1: "Let's Start Your",
      title2: 'Project',
      description: 'Fill out the form and a specialist will contact you to schedule a no-obligation consultation.',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        phone: 'Phone',
        message: 'Tell us about your project...',
        submit: 'Send Message',
        submitting: 'Sending...',
      },
      info: {
        phone: 'Phone',
        email: 'Email',
        location: 'Location',
        hours: 'Business Hours',
        weekdays: 'Monday - Friday',
        saturday: 'Saturday',
      },
    },
    // CTA Section
    cta: {
      tagline: 'Get Started',
      title1: 'Ready to Transform',
      title2: 'Your Home?',
      description: 'Schedule your free consultation and let\'s bring your vision to life.',
      button: 'Request Consultation',
    },
    // Footer
    footer: {
      tagline: 'Premium Residential Remodeling',
      description: 'Transforming homes across Philadelphia with precision, quality, and unwavering professionalism.',
      quickLinks: 'Quick Links',
      services: 'Services',
      contactInfo: 'Contact',
      rights: 'All rights reserved.',
      servicesList: ['Kitchen Remodeling', 'Bathroom Remodeling', 'Full Renovations', 'Interior Painting', 'Custom Carpentry'],
    },
    // Mobile CTA
    mobileCta: {
      startProject: 'Start Project',
      call: 'Call Now',
    },
    // Loader
    loader: {
      loading: 'Loading',
    },
    // Video Showcase
    video: {
      tagline: 'Watch Us Work',
      title1: 'More Than',
      title2: 'Renovation',
      description: 'See our expert team transform spaces with precision craftsmanship and attention to detail.',
      stat1: 'Satisfaction',
      stat2: 'Rating',
    },
    // Process/Approach
    process: {
      tagline: 'How We Work',
      title: 'Our Approach',
      step1: { title: 'Discovery', desc: 'Detailed consultation to understand your vision.' },
      step2: { title: 'Planning', desc: 'Complete planning and budget proposal.' },
      step3: { title: 'Execution', desc: 'Professional implementation with constant updates.' },
      step4: { title: 'Delivery', desc: 'Final walkthrough and satisfaction guarantee.' },
    },
    // Services Page
    servicesPage: {
      heroTagline: 'Our Services',
      heroTitle1: 'Premium Residential',
      heroTitle2: 'Remodeling',
      heroTitleSuffix: 'in Philadelphia',
      heroDescription: 'From concept to completion, we deliver exceptional craftsmanship with meticulous attention to detail, transparent communication, and unwavering commitment to your satisfaction.',
      ctaConsultation: 'Schedule a Free Consultation',
      ctaViewServices: 'View Our Services',
      servicesTagline: 'What We Do',
      servicesTitle: 'Comprehensive Remodeling Services',
      learnMore: 'Learn More',
      processTagline: 'How We Work',
      processTitle: 'Our Process',
      ctaTagline: 'Get Started',
      ctaTitle1: 'Ready to Transform',
      ctaTitle2: 'Your Home?',
      ctaDescription: 'Schedule your free consultation today and let\'s discuss how we can bring your vision to life with precision, quality, and professionalism.',
      ctaButton: 'Book Your Free Consultation',
      services: [
        { title: 'Kitchen Remodeling', description: 'Transform your kitchen into a stunning culinary space with premium materials, custom cabinetry, and modern appliances integration.' },
        { title: 'Bathroom Remodeling', description: 'Create a spa-like retreat with luxury fixtures, marble finishes, and thoughtful layouts that maximize both beauty and functionality.' },
        { title: 'Basement Finishing', description: 'Convert your basement into valuable living space - entertainment rooms, home offices, or guest suites with professional-grade finishing.' },
        { title: 'Interior & Exterior Painting', description: 'Professional painting services with premium paints, meticulous preparation, and flawless finishes that transform your spaces.' },
        { title: 'Drywall Installation & Repair', description: 'Expert drywall services from new construction to seamless repairs, ensuring perfectly smooth walls ready for any finish.' },
        { title: 'Flooring Installation', description: 'Premium flooring solutions including hardwood, LVP, and tile - professionally installed for lasting beauty and durability.' },
        { title: 'General Home Renovations', description: 'Comprehensive renovation services that transform outdated spaces into modern, functional areas tailored to your lifestyle.' },
        { title: 'Decks & Exterior Improvements', description: 'Extend your living space outdoors with custom decks, patios, and exterior upgrades designed for Philadelphia weather.' },
        { title: 'Custom Carpentry', description: 'Bespoke woodwork solutions including built-ins, custom shelving, trim work, and architectural details that add character.' },
        { title: 'Full Home Remodeling', description: 'Complete home transformations managed end-to-end with dedicated project oversight, timeline adherence, and quality assurance.' },
      ],
      processSteps: [
        { step: '01', title: 'Consultation', description: 'Free in-home assessment to understand your vision, needs, and project scope.' },
        { step: '02', title: 'Planning & Design', description: 'Detailed project planning with timelines, materials selection, and comprehensive estimates.' },
        { step: '03', title: 'Execution', description: 'Professional implementation with daily progress updates and quality control checkpoints.' },
        { step: '04', title: 'Quality Inspection', description: 'Rigorous inspection at every phase ensuring work meets our exacting standards.' },
        { step: '05', title: 'Final Walkthrough', description: 'Comprehensive review with you to ensure complete satisfaction before project completion.' },
      ],
    },
    // About Page
    aboutPage: {
      heroTagline: 'About Us',
      heroTitle1: 'Built on Precision,',
      heroTitle2: 'Quality',
      heroTitleSuffix: 'and Trust',
      heroDescription: 'Martinez Solutions is a premier residential contracting firm dedicated to transforming homes across Philadelphia with exceptional craftsmanship and unwavering professionalism.',
      ctaButton: 'Start Your Project',
      storyTagline: 'Our Story',
      storyTitle1: 'A Tradition of',
      storyTitle2: 'Excellence',
      storyYears: 'Years Combined Experience',
      storyP1: 'was founded with a clear vision: to bring a new standard of professionalism and quality to residential remodeling in Philadelphia. With over 15 years of combined experience, our team has completed hundreds of successful projects.',
      storyP2: 'We specialize in premium residential renovations, from complete home transformations to targeted improvements. Every project benefits from our structured approach: detailed planning, transparent communication, and meticulous execution.',
      storyP3: 'Our commitment goes beyond just completing projects—we build lasting relationships with our clients through trust, reliability, and exceptional results that exceed expectations.',
      storyPoints: ['500+ Projects Completed', 'Licensed & Insured', '100% Satisfaction Focus', 'Premium Materials'],
      valuesTagline: 'What Drives Us',
      valuesTitle: 'Our Core Values',
      values: [
        { title: 'Precision', description: 'Meticulous attention to every detail in planning and execution.' },
        { title: 'Transparency', description: 'Clear communication and no surprises throughout your project.' },
        { title: 'Excellence', description: 'Premium materials and superior craftsmanship in everything we do.' },
        { title: 'Reliability', description: 'On-time delivery and adherence to agreed timelines.' },
      ],
      teamTagline: 'The People Behind Our Success',
      teamTitle: 'Our Team',
      team: [
        { name: 'Project Director', role: 'Founder & Leadership', description: 'Over 15 years of experience leading residential remodeling projects across Philadelphia.' },
        { name: 'Operations Manager', role: 'Field Supervisor', description: 'Ensuring every project runs smoothly with meticulous attention to timelines and quality.' },
        { name: 'Lead Craftsman', role: 'Skilled Remodeling Team', description: 'Expert craftsmen delivering precision work across all trades and specialties.' },
      ],
      certTagline: 'Trust & Credibility',
      certTitle: 'Certifications & Standards',
      certifications: [
        { title: 'Licensed & Insured', description: 'Full coverage for your peace of mind' },
        { title: 'Code-Compliant Work', description: 'All work meets local building codes' },
        { title: 'Safety Standards', description: 'OSHA-compliant work practices' },
        { title: 'Satisfaction Guarantee', description: 'Your complete satisfaction assured' },
      ],
      ctaTagline: 'Ready to Start?',
      ctaTitle1: "Let's Build Something",
      ctaTitle2: 'Exceptional Together',
      ctaDescription: 'Your vision deserves a team that understands quality, values your time, and delivers results that exceed expectations.',
      ctaRequestButton: 'Request a Consultation',
    },
    // Contact Page
    contactPage: {
      heroTagline: 'Contact Us',
      heroTitle1: 'Start Your Project',
      heroTitle2: 'With Confidence',
      heroDescription: 'Schedule your free consultation and take the first step toward transforming your home with Philadelphia\'s premier remodeling team.',
      formTitle: 'Request a Consultation',
      formLabels: {
        name: 'Full Name',
        phone: 'Phone',
        email: 'Email',
        address: 'Project Address',
        serviceType: 'Service Type',
        budget: 'Estimated Budget',
        timeline: 'Estimated Timeline',
        message: 'Project Description',
        files: 'Upload Photos (Optional)',
      },
      formPlaceholders: {
        name: 'John Smith',
        phone: '(215) 555-0123',
        email: 'john@example.com',
        address: '123 Main St, Philadelphia, PA',
        serviceType: 'Select a service...',
        budget: 'Select budget range...',
        timeline: 'Select timeline...',
        message: 'Tell us about your project vision, specific requirements, or any questions you have...',
        files: 'Click to upload project photos',
      },
      formRequired: 'required',
      submitButton: 'Schedule My Consultation',
      submitting: 'Submitting...',
      successTitle: 'Thank You!',
      successMessage: 'Your consultation request has been received. Our team will contact you within 24 business hours to schedule your free in-home consultation.',
      contactInfoTitle: 'Contact Information',
      phone: 'Phone',
      email: 'Email',
      serviceArea: 'Service Area',
      serviceAreaValue: 'Philadelphia & Surrounding Counties',
      businessHours: 'Business Hours',
      weekdays: 'Monday - Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
      closed: 'Closed',
      mapTitle: 'Our Service Area',
      serviceOptions: [
        'Kitchen Remodeling',
        'Bathroom Remodeling',
        'Basement Finishing',
        'Interior & Exterior Painting',
        'Drywall Installation & Repair',
        'Flooring Installation',
        'General Home Renovations',
        'Decks & Exterior Improvements',
        'Custom Carpentry',
        'Full Home Remodeling',
      ],
      budgetOptions: [
        '$5,000 – $15,000',
        '$15,000 – $50,000',
        '$50,000 – $100,000',
        '$100,000+',
      ],
      timelineOptions: [
        'As soon as possible',
        'Within 1 month',
        '1-3 months',
        '3-6 months',
        'Flexible / Planning ahead',
      ],
      remove: 'Remove',
    },
  },
  es: {
    // Navbar
    nav: {
      home: 'Inicio',
      services: 'Servicios',
      projects: 'Proyectos',
      about: 'Nosotros',
      contact: 'Contacto',
      consultation: 'Consulta',
      startProject: 'Iniciar Proyecto',
    },
    // Hero
    hero: {
      tagline: 'Contratista Residencial Premium',
      title1: 'Construyendo',
      title2: 'Legados de Lujo',
      subtitle: 'Renovaciones Residenciales Premium en Philadelphia',
      cta1: 'Iniciar Proyecto',
      cta2: 'Ver Proyectos',
      scroll: 'Scroll',
      stat1Label: 'Proyectos',
      stat2Label: 'Años',
      stat3Label: 'Satisfacción',
    },
    // Services Section (Home)
    services: {
      tagline: 'Nuestros Servicios',
      title: 'Qué Gestionamos',
      service1: { title: 'Cocinas de Autor', subtitle: 'Kitchen Design' },
      service2: { title: 'Baños Spa', subtitle: 'Luxury Bathrooms' },
      service3: { title: 'Renovaciones Integrales', subtitle: 'Full Renovations' },
      extras: ['Pintura', 'Drywall', 'Mantenimiento', 'Iluminación'],
    },
    // Projects Section
    projects: {
      tagline: 'Portafolio',
      title: 'Proyectos Destacados',
      viewAll: 'Ver Todos',
      project1: { title: 'Residencia Chestnut Hill', category: 'Renovación Integral' },
      project2: { title: 'Penthouse Rittenhouse', category: 'Cocina de Autor' },
      project3: { title: 'Villa Society Hill', category: 'Baño Spa' },
      project4: { title: 'Townhouse Washington Square', category: 'Sala de Estar' },
    },
    // Why Choose Us
    whyUs: {
      tagline: 'Nuestra Diferencia',
      title: 'Por Qué Elegirnos',
      reason1: { title: 'Precisión', subtitle: 'Precision', desc: 'Documentación clara de entregables y especificaciones técnicas antes del inicio.' },
      reason2: { title: 'Transparencia', subtitle: 'Transparency', desc: 'Comunicación directa, actualizaciones regulares y respuesta estructurada.' },
      reason3: { title: 'Calidad', subtitle: 'Quality', desc: 'Materiales premium y acabados de especificación arquitectónica.' },
      reason4: { title: 'Puntualidad', subtitle: 'Timeliness', desc: 'Cronogramas realistas con hitos claros y cumplimiento de fechas.' },
      reason5: { title: 'Compromiso', subtitle: 'Commitment', desc: 'Supervisión continua para garantizar adherencia a estándares de calidad.' },
    },
    // About Section
    about: {
      tagline: 'Sobre Nosotros',
      title1: 'Construyendo',
      title2: 'Excelencia',
      description1: 'es una empresa de contratación residencial establecida en Philadelphia, especializada en la gestión profesional de proyectos de mejoras interiores y renovaciones de lujo.',
      description2: 'Operamos con una estructura de supervisión continua, procesos documentados y compromiso con la ejecución precisa según especificaciones establecidas.',
      description3: 'Nuestra misión es proporcionar servicios residenciales con el nivel de profesionalismo, transparencia y control de proyecto que nuestros clientes esperan de un contratista establecido.',
      cta: 'Conócenos',
      values: {
        v1: { title: 'Licencia y Seguro', desc: 'Operamos con todas las licencias requeridas y cobertura completa.' },
        v2: { title: 'Estándares de Calidad', desc: 'Adherencia estricta a códigos de construcción y mejores prácticas.' },
        v3: { title: 'Equipo Profesional', desc: 'Personal capacitado con experiencia comprobada en sus especialidades.' },
        v4: { title: 'Compromiso Total', desc: 'Enfoque en cumplimiento de cronogramas y presupuestos acordados.' },
      },
    },
    // Contact Section
    contact: {
      tagline: 'Contacto',
      title1: 'Iniciemos su',
      title2: 'Proyecto',
      description: 'Complete el formulario y un especialista se comunicará con usted para agendar una consulta sin compromiso.',
      form: {
        name: 'Nombre Completo',
        email: 'Correo Electrónico',
        phone: 'Teléfono',
        message: 'Cuéntenos sobre su proyecto...',
        submit: 'Enviar Mensaje',
        submitting: 'Enviando...',
      },
      info: {
        phone: 'Teléfono',
        email: 'Correo',
        location: 'Ubicación',
        hours: 'Horario de Atención',
        weekdays: 'Lunes - Viernes',
        saturday: 'Sábado',
      },
    },
    // CTA Section
    cta: {
      tagline: 'Comenzar',
      title1: '¿Listo para Transformar',
      title2: 'Su Hogar?',
      description: 'Agende su consulta gratuita y hagamos realidad su visión.',
      button: 'Solicitar Consulta',
    },
    // Footer
    footer: {
      tagline: 'Remodelación Residencial Premium',
      description: 'Transformando hogares en Philadelphia con precisión, calidad y profesionalismo inquebrantable.',
      quickLinks: 'Enlaces Rápidos',
      services: 'Servicios',
      contactInfo: 'Contacto',
      rights: 'Todos los derechos reservados.',
      servicesList: ['Remodelación de Cocinas', 'Remodelación de Baños', 'Renovaciones Completas', 'Pintura Interior', 'Carpintería Personalizada'],
    },
    // Mobile CTA
    mobileCta: {
      startProject: 'Iniciar Proyecto',
      call: 'Llamar Ahora',
    },
    // Loader
    loader: {
      loading: 'Cargando',
    },
    // Video Showcase
    video: {
      tagline: 'Véanos Trabajar',
      title1: 'Más Que',
      title2: 'Renovación',
      description: 'Vea a nuestro equipo experto transformar espacios con artesanía de precisión y atención al detalle.',
      stat1: 'Satisfacción',
      stat2: 'Calificación',
    },
    // Process/Approach
    process: {
      tagline: 'Cómo Trabajamos',
      title: 'Nuestro Enfoque',
      step1: { title: 'Descubrimiento', desc: 'Consulta detallada para entender su visión.' },
      step2: { title: 'Planificación', desc: 'Planificación completa y propuesta de presupuesto.' },
      step3: { title: 'Ejecución', desc: 'Implementación profesional con actualizaciones constantes.' },
      step4: { title: 'Entrega', desc: 'Recorrido final y garantía de satisfacción.' },
    },
    // Services Page
    servicesPage: {
      heroTagline: 'Nuestros Servicios',
      heroTitle1: 'Remodelación Residencial',
      heroTitle2: 'Premium',
      heroTitleSuffix: 'en Philadelphia',
      heroDescription: 'Desde el concepto hasta la finalización, entregamos artesanía excepcional con atención meticulosa al detalle, comunicación transparente y compromiso inquebrantable con su satisfacción.',
      ctaConsultation: 'Agendar Consulta Gratuita',
      ctaViewServices: 'Ver Nuestros Servicios',
      servicesTagline: 'Qué Hacemos',
      servicesTitle: 'Servicios Completos de Remodelación',
      learnMore: 'Más Información',
      processTagline: 'Cómo Trabajamos',
      processTitle: 'Nuestro Proceso',
      ctaTagline: 'Comenzar',
      ctaTitle1: '¿Listo para Transformar',
      ctaTitle2: 'Su Hogar?',
      ctaDescription: 'Agende su consulta gratuita hoy y hablemos de cómo podemos hacer realidad su visión con precisión, calidad y profesionalismo.',
      ctaButton: 'Reserve Su Consulta Gratuita',
      services: [
        { title: 'Remodelación de Cocinas', description: 'Transforme su cocina en un espacio culinario impresionante con materiales premium, gabinetes personalizados e integración de electrodomésticos modernos.' },
        { title: 'Remodelación de Baños', description: 'Cree un refugio tipo spa con accesorios de lujo, acabados de mármol y diseños pensados que maximizan tanto la belleza como la funcionalidad.' },
        { title: 'Acabado de Sótanos', description: 'Convierta su sótano en un valioso espacio habitable - salas de entretenimiento, oficinas en casa o suites de huéspedes con acabados de grado profesional.' },
        { title: 'Pintura Interior y Exterior', description: 'Servicios profesionales de pintura con pinturas premium, preparación meticulosa y acabados impecables que transforman sus espacios.' },
        { title: 'Instalación y Reparación de Drywall', description: 'Servicios expertos de drywall desde nueva construcción hasta reparaciones perfectas, asegurando paredes perfectamente lisas listas para cualquier acabado.' },
        { title: 'Instalación de Pisos', description: 'Soluciones premium de pisos incluyendo madera dura, LVP y azulejos - instalados profesionalmente para belleza y durabilidad duraderas.' },
        { title: 'Renovaciones Generales', description: 'Servicios integrales de renovación que transforman espacios obsoletos en áreas modernas y funcionales adaptadas a su estilo de vida.' },
        { title: 'Terrazas y Mejoras Exteriores', description: 'Extienda su espacio habitable al exterior con terrazas personalizadas, patios y mejoras exteriores diseñadas para el clima de Philadelphia.' },
        { title: 'Carpintería Personalizada', description: 'Soluciones de carpintería a medida incluyendo empotrados, estanterías personalizadas, molduras y detalles arquitectónicos que añaden carácter.' },
        { title: 'Remodelación Completa', description: 'Transformaciones completas del hogar gestionadas de principio a fin con supervisión dedicada del proyecto, cumplimiento de plazos y garantía de calidad.' },
      ],
      processSteps: [
        { step: '01', title: 'Consulta', description: 'Evaluación gratuita en su hogar para entender su visión, necesidades y alcance del proyecto.' },
        { step: '02', title: 'Planificación y Diseño', description: 'Planificación detallada del proyecto con cronogramas, selección de materiales y presupuestos completos.' },
        { step: '03', title: 'Ejecución', description: 'Implementación profesional con actualizaciones diarias de progreso y puntos de control de calidad.' },
        { step: '04', title: 'Inspección de Calidad', description: 'Inspección rigurosa en cada fase asegurando que el trabajo cumpla con nuestros estándares exigentes.' },
        { step: '05', title: 'Recorrido Final', description: 'Revisión completa con usted para asegurar satisfacción total antes de la finalización del proyecto.' },
      ],
    },
    // About Page
    aboutPage: {
      heroTagline: 'Sobre Nosotros',
      heroTitle1: 'Construido sobre Precisión,',
      heroTitle2: 'Calidad',
      heroTitleSuffix: 'y Confianza',
      heroDescription: 'Martinez Solutions es una firma de contratación residencial premier dedicada a transformar hogares en Philadelphia con artesanía excepcional y profesionalismo inquebrantable.',
      ctaButton: 'Iniciar Su Proyecto',
      storyTagline: 'Nuestra Historia',
      storyTitle1: 'Una Tradición de',
      storyTitle2: 'Excelencia',
      storyYears: 'Años de Experiencia Combinada',
      storyP1: 'fue fundada con una visión clara: traer un nuevo estándar de profesionalismo y calidad a la remodelación residencial en Philadelphia. Con más de 15 años de experiencia combinada, nuestro equipo ha completado cientos de proyectos exitosos.',
      storyP2: 'Nos especializamos en renovaciones residenciales premium, desde transformaciones completas del hogar hasta mejoras específicas. Cada proyecto se beneficia de nuestro enfoque estructurado: planificación detallada, comunicación transparente y ejecución meticulosa.',
      storyP3: 'Nuestro compromiso va más allá de solo completar proyectos—construimos relaciones duraderas con nuestros clientes a través de confianza, confiabilidad y resultados excepcionales que superan las expectativas.',
      storyPoints: ['500+ Proyectos Completados', 'Licenciados y Asegurados', 'Enfoque 100% en Satisfacción', 'Materiales Premium'],
      valuesTagline: 'Lo Que Nos Impulsa',
      valuesTitle: 'Nuestros Valores Fundamentales',
      values: [
        { title: 'Precisión', description: 'Atención meticulosa a cada detalle en la planificación y ejecución.' },
        { title: 'Transparencia', description: 'Comunicación clara y sin sorpresas durante todo su proyecto.' },
        { title: 'Excelencia', description: 'Materiales premium y artesanía superior en todo lo que hacemos.' },
        { title: 'Confiabilidad', description: 'Entrega puntual y adherencia a los plazos acordados.' },
      ],
      teamTagline: 'Las Personas Detrás de Nuestro Éxito',
      teamTitle: 'Nuestro Equipo',
      team: [
        { name: 'Director de Proyectos', role: 'Fundador y Liderazgo', description: 'Más de 15 años de experiencia liderando proyectos de remodelación residencial en Philadelphia.' },
        { name: 'Gerente de Operaciones', role: 'Supervisor de Campo', description: 'Asegurando que cada proyecto funcione sin problemas con atención meticulosa a los plazos y la calidad.' },
        { name: 'Artesano Principal', role: 'Equipo de Remodelación Experto', description: 'Artesanos expertos entregando trabajo de precisión en todos los oficios y especialidades.' },
      ],
      certTagline: 'Confianza y Credibilidad',
      certTitle: 'Certificaciones y Estándares',
      certifications: [
        { title: 'Licenciados y Asegurados', description: 'Cobertura completa para su tranquilidad' },
        { title: 'Trabajo Conforme a Código', description: 'Todo el trabajo cumple con los códigos de construcción locales' },
        { title: 'Estándares de Seguridad', description: 'Prácticas de trabajo conformes a OSHA' },
        { title: 'Garantía de Satisfacción', description: 'Su satisfacción completa asegurada' },
      ],
      ctaTagline: '¿Listo para Comenzar?',
      ctaTitle1: 'Construyamos Algo',
      ctaTitle2: 'Excepcional Juntos',
      ctaDescription: 'Su visión merece un equipo que entiende la calidad, valora su tiempo y entrega resultados que superan las expectativas.',
      ctaRequestButton: 'Solicitar una Consulta',
    },
    // Contact Page
    contactPage: {
      heroTagline: 'Contáctenos',
      heroTitle1: 'Inicie Su Proyecto',
      heroTitle2: 'Con Confianza',
      heroDescription: 'Agende su consulta gratuita y dé el primer paso hacia la transformación de su hogar con el equipo de remodelación premier de Philadelphia.',
      formTitle: 'Solicitar una Consulta',
      formLabels: {
        name: 'Nombre Completo',
        phone: 'Teléfono',
        email: 'Correo Electrónico',
        address: 'Dirección del Proyecto',
        serviceType: 'Tipo de Servicio',
        budget: 'Presupuesto Estimado',
        timeline: 'Tiempo Estimado',
        message: 'Descripción del Proyecto',
        files: 'Subir Fotos (Opcional)',
      },
      formPlaceholders: {
        name: 'Juan Pérez',
        phone: '(215) 555-0123',
        email: 'juan@ejemplo.com',
        address: '123 Calle Principal, Philadelphia, PA',
        serviceType: 'Seleccione un servicio...',
        budget: 'Seleccione rango de presupuesto...',
        timeline: 'Seleccione tiempo...',
        message: 'Cuéntenos sobre la visión de su proyecto, requisitos específicos o cualquier pregunta que tenga...',
        files: 'Haga clic para subir fotos del proyecto',
      },
      formRequired: 'requerido',
      submitButton: 'Agendar Mi Consulta',
      submitting: 'Enviando...',
      successTitle: '¡Gracias!',
      successMessage: 'Su solicitud de consulta ha sido recibida. Nuestro equipo se comunicará con usted dentro de 24 horas hábiles para agendar su consulta gratuita en su hogar.',
      contactInfoTitle: 'Información de Contacto',
      phone: 'Teléfono',
      email: 'Correo',
      serviceArea: 'Área de Servicio',
      serviceAreaValue: 'Philadelphia y Condados Cercanos',
      businessHours: 'Horario de Atención',
      weekdays: 'Lunes - Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo',
      closed: 'Cerrado',
      mapTitle: 'Nuestra Área de Servicio',
      serviceOptions: [
        'Remodelación de Cocinas',
        'Remodelación de Baños',
        'Acabado de Sótanos',
        'Pintura Interior y Exterior',
        'Instalación y Reparación de Drywall',
        'Instalación de Pisos',
        'Renovaciones Generales',
        'Terrazas y Mejoras Exteriores',
        'Carpintería Personalizada',
        'Remodelación Completa',
      ],
      budgetOptions: [
        '$5,000 – $15,000',
        '$15,000 – $50,000',
        '$50,000 – $100,000',
        '$100,000+',
      ],
      timelineOptions: [
        'Lo antes posible',
        'Dentro de 1 mes',
        '1-3 meses',
        '3-6 meses',
        'Flexible / Planificando a futuro',
      ],
      remove: 'Eliminar',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('language') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t, translations: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default translations;
